import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/context/AuthContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Pricing from "@/pages/Pricing";
import BillingSuccess from "@/pages/BillingSuccess";
import DashboardLayout from "@/pages/DashboardLayout";
import BrandService from "@/pages/BrandService";

import Terms from "@/pages/legal/Terms";
import Privacy from "@/pages/legal/Privacy";
import CookiesPolicy from "@/pages/legal/Cookies";
import ServiceContract from "@/pages/legal/ServiceContract";
import DPA from "@/pages/legal/DPA";
import CookieBanner from "@/components/CookieBanner";

import Overview from "@/pages/dashboard/Overview";
import CalendarPage from "@/pages/dashboard/Calendar";
import Generator from "@/pages/dashboard/Generator";
import Images from "@/pages/dashboard/Images";
import Feed from "@/pages/dashboard/Feed";
import ContentBank from "@/pages/dashboard/ContentBank";
import Analytics from "@/pages/dashboard/Analytics";
import Competitors from "@/pages/dashboard/Competitors";
import SocialListening from "@/pages/dashboard/SocialListening";
import SocialAccounts from "@/pages/dashboard/SocialAccounts";
import BrandConfig from "@/pages/dashboard/BrandConfig";
import Referrals from "@/pages/dashboard/Referrals";
import Subscription from "@/pages/dashboard/Subscription";
import AdminMRR from "@/pages/dashboard/AdminMRR";
import AdminUsers from "@/pages/dashboard/AdminUsers";
import AdminSupport from "@/pages/dashboard/AdminSupport";
import AdminCodeChanges from "@/pages/dashboard/AdminCodeChanges";
import Invoices from "@/pages/dashboard/Invoices";
import SuperAdmin from "@/pages/dashboard/SuperAdmin";
import OnboardingWizard from "@/pages/OnboardingWizard";
import UserSupport from "@/pages/dashboard/UserSupport";
import Start from "@/pages/dashboard/Start";
import BrandBuilder from "@/pages/dashboard/BrandBuilder";
import BrandsOverview from "@/pages/dashboard/BrandsOverview";
import Support from "@/pages/Support";
import Videos from "@/pages/dashboard/Videos";
import Campaigns from "@/pages/dashboard/Campaigns";
import CampaignWizard from "@/pages/dashboard/CampaignWizard";
import CampaignDetail from "@/pages/dashboard/CampaignDetail";
import Security from "@/pages/dashboard/Security";
import ApiKeys from "@/pages/dashboard/ApiKeys";
import Marketplace from "@/pages/dashboard/Marketplace";
import SEO from "@/pages/dashboard/SEO";

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
      <AuthProvider>
        <CurrencyProvider>
        <BrowserRouter>
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: "#0A0A0A",
                color: "white",
                borderRadius: 0,
                border: "1px solid #0A0A0A",
                fontFamily: "JetBrains Mono, monospace",
                fontSize: 12,
              },
            }}
          />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/onboarding" element={
              <ProtectedRoute><OnboardingWizard /></ProtectedRoute>
            } />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/brand-service" element={<BrandService />} />
            <Route path="/legal/terms" element={<Terms />} />
            <Route path="/legal/privacy" element={<Privacy />} />
            <Route path="/legal/cookies" element={<CookiesPolicy />} />
            <Route path="/legal/contract" element={<ServiceContract />} />
            <Route path="/legal/dpa" element={<DPA />} />
            <Route path="/support" element={<Support />} />
            <Route
              path="/billing/success"
              element={
                <ProtectedRoute>
                  <BillingSuccess />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Overview />} />
              <Route path="start" element={<Start />} />
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="generator" element={<Generator />} />
              <Route path="images" element={<Images />} />
              <Route path="videos" element={<Videos />} />
              <Route path="campaigns" element={<Campaigns />} />
              <Route path="campaigns/new" element={<CampaignWizard />} />
              <Route path="campaigns/:id" element={<CampaignDetail />} />
              <Route path="feed" element={<Feed />} />
              <Route path="bank" element={<ContentBank />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="competitors" element={<Competitors />} />
              <Route path="listening" element={<SocialListening />} />
              <Route path="accounts" element={<SocialAccounts />} />
              <Route path="referrals" element={<Referrals />} />
              <Route path="subscription" element={<Subscription />} />
              <Route path="brand" element={<BrandConfig />} />
              <Route path="brands" element={<BrandsOverview />} />
              <Route path="brand-builder" element={<BrandBuilder />} />
              <Route path="support" element={<UserSupport />} />
              <Route path="security" element={<Security />} />
              <Route path="api-keys" element={<ApiKeys />} />
              <Route path="marketplace" element={<Marketplace />} />
              <Route path="seo" element={<SEO />} />
              <Route path="admin" element={<AdminMRR />} />
              <Route path="admin/users" element={<AdminUsers />} />
              <Route path="admin/support" element={<AdminSupport />} />
              <Route path="admin/code-changes" element={<AdminCodeChanges />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="super-admin" element={<SuperAdmin />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <CookieBanner />
        </BrowserRouter>
        </CurrencyProvider>
      </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
